import {
  Fragment,
  useCallback,
  useEffect,
  useState,
} from 'react'
import styled from 'styled-components'

import ModalContainer from './modals/ModalContainer'
import ServiceUnavailablePage from '../components/errors/ServiceUnavailablePage'
import AppRoutes from './AppRoutes'
import { getCurrentMember } from '../features/profile'
import { API_UNAVAILABLE_EVENT } from '../constants/events'
import { getApiErrorMessage } from '../utils/apiError'
import { useAuthSessionRevision } from '../hooks/useAuthSession'
import { restoreAuthSession } from '../services/api'

const APP_TITLE = 'akive — 학습의 모든 순간을 하나로'

function AppContent() {
  const authSessionRevision = useAuthSessionRevision()
  const [isAuthReady, setIsAuthReady] = useState(false)
  const [isApiUnavailable, setIsApiUnavailable] = useState(false)
  const [isRetrying, setIsRetrying] = useState(false)
  const [retryError, setRetryError] = useState('')

  useEffect(() => {
    document.title = APP_TITLE
  }, [])

  useEffect(() => {
    const showServiceUnavailable = () => {
      setRetryError('')
      setIsApiUnavailable(true)
    }
    window.addEventListener(API_UNAVAILABLE_EVENT, showServiceUnavailable)
    return () => window.removeEventListener(API_UNAVAILABLE_EVENT, showServiceUnavailable)
  }, [])

  useEffect(() => {
    let active = true

    restoreAuthSession().finally(() => {
      if (active) setIsAuthReady(true)
    })

    return () => { active = false }
  }, [])

  const retryApiConnection = useCallback(async () => {
    setIsRetrying(true)
    setRetryError('')
    try {
      await getCurrentMember()
      setIsApiUnavailable(false)
    } catch (error) {
      if (error.response) {
        // HTTP 응답을 받았다면 서버 연결 자체는 복구된 상태다.
        setIsApiUnavailable(false)
      } else {
        setRetryError(getApiErrorMessage(error))
      }
    } finally {
      setIsRetrying(false)
    }
  }, [])

  if (!isAuthReady) {
    return (
      <AuthLoadingRoot
        role="status"
        aria-live="polite"
      >
        <AuthLoadingContent>
          <LearningFlow aria-hidden="true">
            <LearningNode />
            <LearningLink />
            <LearningNode $delay="200ms" />
            <LearningLink $delay="200ms" />
            <LearningNode $delay="400ms" />
          </LearningFlow>
          <AuthLoadingMessage>학습의 모든 순간을 연결하고 있어요</AuthLoadingMessage>
        </AuthLoadingContent>
      </AuthLoadingRoot>
    )
  }

  return (
    <Fragment key={authSessionRevision}>
      <AppRoutes />
      <ModalContainer />
      {isApiUnavailable && (
        <ServiceUnavailablePage
          isRetrying={isRetrying}
          retryError={retryError}
          onRetry={retryApiConnection}
        />
      )}
    </Fragment>
  )
}

export default AppContent

const AuthLoadingRoot = styled.main`
  display: grid;
  min-height: 100dvh;
  place-items: center;
  background: ${({ theme }) => theme.colors.background};
`

const AuthLoadingContent = styled.div`
  display: grid;
  justify-items: center;
  gap: 24px;
  opacity: 0;
  transform: scale(1.2);
  transform-origin: center;
  animation: loading-reveal 160ms ease 240ms forwards;

  @keyframes loading-reveal {
    to { opacity: 1; }
  }

  @media (prefers-reduced-motion: reduce) {
    opacity: 1;
    animation: none;
  }
`

const LearningFlow = styled.div`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.primary};
  transform: scale(0.8);
  transform-origin: center;
`

const LearningNode = styled.span`
  width: 20px;
  height: 20px;
  border: 3px solid currentColor;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.background};
  animation: node-pulse 1.6s ease-in-out ${({ $delay }) => $delay || '0ms'} infinite;

  @keyframes node-pulse {
    0%, 100% { transform: scale(1); opacity: 0.45; }
    45% { transform: scale(1.25); opacity: 1; }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

const LearningLink = styled.span`
  width: 72px;
  height: 3px;
  background: currentColor;
  transform-origin: left;
  animation: link-connect 1.6s ease-in-out ${({ $delay }) => $delay || '0ms'} infinite;

  @keyframes link-connect {
    0%, 20% { transform: scaleX(0); opacity: 0; }
    55%, 100% { transform: scaleX(1); opacity: 0.7; }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

const AuthLoadingMessage = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 9px;
  font-weight: 500;
  line-height: 1.5;
  letter-spacing: -0.01em;
`
